import React, {
  FC, useState, useCallback, useEffect,
} from 'react';
import PortalHeader from '../../assessment-portal/components/portal-header';
import {
  MOBILE_PRIMARY_CTA_CLASS,
  MOBILE_PRIMARY_CTA_DISABLED_CLASS,
  MOBILE_PRIMARY_CTA_ENABLED_CLASS,
} from '../../assessment-portal/components/primary-cta';
import CombinedConsent from '../components/combined-consent';
import { CARE_PATHWAY_CONSENT } from '../data/care-pathway-consent';
import type { CarePathwayState } from '../types/flow';

interface CarePathwayPageProps {
  defaultValues?: CarePathwayState
  onBack: () => void
  onNext: (values: CarePathwayState) => void
}

const CarePathwayPage: FC<CarePathwayPageProps> = ({
  defaultValues,
  onBack,
  onNext,
}) => {
  const [isAccepted, setIsAccepted] = useState(
    defaultValues?.isAccepted ?? false,
  );

  useEffect(() => {
    setIsAccepted(defaultValues?.isAccepted ?? false);
  }, [defaultValues?.isAccepted]);

  const handleAcceptChange = useCallback(
    (accepted: boolean) => {
      setIsAccepted(accepted);
    },
    [],
  );

  const handleBack = useCallback(() => {
    onBack();
  }, [onBack]);

  const handleContinue = useCallback(() => {
    if (!isAccepted) return;
    onNext({ isAccepted });
  }, [isAccepted, onNext]);

  const handleMobileCheckboxChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      handleAcceptChange(event.target.checked);
    },
    [handleAcceptChange],
  );

  return (
    <div className="adhd-portal min-h-screen flex flex-col bg-page">
      <PortalHeader
        title="Your care pathway"
        subtitle="Read before you continue"
        chip="Before you start"
        chipTone="grey"
        progress={0}
        onBack={handleBack}
      />
      <main
        className="flex-1 flex items-start justify-center px-0 md:px-4 py-6 md:py-8 md:pb-8"
      >
        <div className="w-full max-w-3xl px-4 md:px-0">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold mb-2 text-brand-dark">
              Understanding Your Care Pathway
            </h1>
            <p className="text-slate-600 text-sm">
              Please review this important information
              about how your care team works together
            </p>
          </div>

          <CombinedConsent
            isAccepted={isAccepted}
            onAcceptChange={handleAcceptChange}
          />

          <nav
            className="hidden md:flex mt-6 gap-3"
            aria-label="Page navigation"
          >
            <button
              type="button"
              onClick={handleBack}
              className={`
                px-5 py-3 rounded-2lg text-sm font-semibold
                border-2 border-slate-200 text-slate-600
                hover:bg-slate-50 cursor-pointer
              `}
            >
              ← Back
            </button>
            <button
              type="button"
              onClick={handleContinue}
              disabled={!isAccepted}
              aria-disabled={!isAccepted}
              className={`
                flex-1 py-3 rounded-2lg font-bold text-sm
                text-white transition-all
                ${isAccepted
                ? 'bg-brand cursor-pointer'
                : 'bg-slate-300 cursor-not-allowed opacity-60'
                }
              `}
            >
              Continue to Consent &amp; Terms →
            </button>
          </nav>
        </div>
      </main>

      <div className="portal-navrow-spacer md:hidden" aria-hidden />
      <div className="portal-navrow--sticky md:hidden">
        <label
          htmlFor="care-pathway-consent-mobile"
          className={`
            flex items-start gap-3 p-3 mb-3 rounded-lg border cursor-pointer
            text-xs text-slate-700 leading-relaxed
            ${isAccepted
            ? 'border-brand bg-brand bg-opacity-5 font-semibold'
            : 'border-slate-200 bg-slate-50'
            }
          `}
        >
          <input
            type="checkbox"
            id="care-pathway-consent-mobile"
            className="w-4 h-4 mt-0.5 cursor-pointer accent-blue-600 flex-shrink-0"
            checked={isAccepted}
            onChange={handleMobileCheckboxChange}
          />
          <span>
            {CARE_PATHWAY_CONSENT.acceptanceStatement}
          </span>
        </label>
        <button
          type="button"
          onClick={handleContinue}
          disabled={!isAccepted}
          aria-disabled={!isAccepted}
          className={`${MOBILE_PRIMARY_CTA_CLASS} ${
            isAccepted ? MOBILE_PRIMARY_CTA_ENABLED_CLASS : MOBILE_PRIMARY_CTA_DISABLED_CLASS
          }`}
        >
          Continue to Consent &amp; Terms →
        </button>
        {!isAccepted && (
          <p className="text-xs text-slate-500 text-center mt-2 leading-snug">
            Tick the box above to continue
          </p>
        )}
      </div>
    </div>
  );
};

export default CarePathwayPage;
